<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, useTemplateRef, watch, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from "lodash";
import Spinner from "@/components/ui/Spinner.vue";
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconLightning from '@/components/icon/IconLightning.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import AddSubSecondStreamModal from '@/components/modals/AddSubSecondStreamModal.vue'
import StreamCardView from '@/components/ui/StreamCardView.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue';
import StreamService from '@/services/StreamService'
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import FormButton from '../../components/Atoms/FormButton.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import ViewModeSwitcher from '../../components/ui/ViewModeSwitcher.vue';
import ResponsiveToolbar from '@/components/ui/ResponsiveToolbar.vue';
import ListingSearchInput from '@/components/ui/ListingSearchInput.vue';
import PageHeader from '@/components/ui/PageHeader.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import ListingCreateTile from '@/components/ui/ListingCreateTile.vue';
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import { sortStreams } from '@/utils'
import { usePersistedSort, DATE_NAME_SORT } from '@/Utils/usePersistedSort'
const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()

const searchWidgetInput = ref('')
const deleteModalConfiguredStream = ref(null)
const deleteProcessing = ref(false)
const sortDropdown = ref(null)

const loadingStatus = computed(() => store.getters["Ui/loadingStatus"]);
const activeTeam = computed(() => store.getters['User/activeTeam'])
const memberPermission = computed(() => activeTeam.value && store.getters['User/userPermissions'])
const subSecondStreams = computed(() => store.getters["Streams/subSecondStreams"]);

// Sort + search only add value once there's a meaningful list to manage.
const showListControls = computed(() => subSecondStreams.value.length > 2);
const { sortBy, setSort } = usePersistedSort('castr.sort.subSecond', DATE_NAME_SORT, 'Newest')
const sortByLabel = computed(() => sortBy.value)
const hasLiveSubscription = computed(() => store.getters["User/hasLiveSubscription"]);
const maxSubSecondStreams = computed(() => store.getters["User/maxSubSecondStreams"]);

const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])

const subSecondStreamsFiltered = computed(() => {
	const searchStr = searchWidgetInput.value.toLowerCase();
	const result = subSecondStreams.value.filter(stream => stream.name.toLowerCase().includes(searchStr))
	return sortStreams(result, sortBy.value) || result
})

const subSecondLimit = computed(() => {
	const c = maxSubSecondStreams.value
	return typeof c === 'number' && c > 0 ? c : null
})

// `used` is this workspace's; the meter pairs it with the account's total, so
// an empty workspace still shows a plan that is being spent elsewhere.
const subSecondHeaderUsage = computed(() => {
	if (loadingStatus.value) return null
	const meta = {
		used: subSecondStreams.value?.length || 0,
		label: 'streams',
		labelSingular: 'stream',
		resource: 'subsecond',
	}
	if (subSecondLimit.value) {
		meta.limit = subSecondLimit.value
		meta.planLabel = 'sub-second streams'
	}
	return meta
})

const atSubSecondLimit = computed(() => {
	const max = subSecondLimit.value
	if (!max) return false
	return (subSecondStreams.value?.length ?? 0) >= max
})

const showSubSecondCreateButton = computed(() =>
	!loadingStatus.value && hasLiveSubscription.value?.enabled && !hasLiveSubscription.value?.trial
)

const isSubSecondCreateDisabled = computed(() =>
	atSubSecondLimit.value || (memberPermission.value && !memberPermission.value.liveStreamManage)
)

const subSecondCreateDisabledTitle = computed(() => {
	if (atSubSecondLimit.value) return planLimitReachedMessage('Sub-second stream')
	return ''
})

watch(
	() => hasLiveSubscription,
	async () => {
		store.dispatch('Ui/setLoadingStatus', true)
		await store.dispatch('Streams/getStreams')
		store.dispatch('Ui/setLoadingStatus', false)
	}, {
	immediate: true
}
)

const changeSortBy = (val) => {
	setSort(val)
	sortDropdown.value?.toggleDropdown()
}

const onNewStream = async (substream) => {
	// Redirect immediately for seamless UX
	router.push({ path: '/sub-second/' + substream._id + '/dashboard' })
	// Refresh store in background (detail page will wait for it)
	store.dispatch('Streams/getStreams', true)
}

const onStreamDeleteRequest = (stream) => {
	deleteModalConfiguredStream.value = stream;
	showModal('delete-stream');
}

const onStreamDeleteConfirm = async () => {
	// pop stream out of the list
	deleteProcessing.value = true;
	try {
		await StreamService.deleteStream(deleteModalConfiguredStream.value._id);
		// Force refresh to remove deleted stream from list
		await store.dispatch('Streams/getStreams', true)
		// track event
		const removedStream = deleteModalConfiguredStream.value
		window.trackEvent('Stream Deleted', {
			streamId: removedStream._id,
			streamName: removedStream.name,
			streamType: removedStream.type,
		});
	} catch (err) {
		notify({ group: 'error', title: err.error, text: err.message });
		// push stream back to list
		onNewStream(deleteModalConfiguredStream.value);
	}

	deleteModalConfiguredStream.value = null
	deleteProcessing.value = false
	notify({
		group: 'success',
		text: 'Deleted successfully'
	})
}

const showCreateSubSecondStreamModal = () => showModal('modal-add-sub-second-stream')
const showTutorialModal = () => showModal('modal_tutorial');
</script>

<template>
	<div class="container detail-shell px-4 page-title-area">
		<PageHeader
			title="Sub-Second Streaming"
			subtitle="Deliver ultra-low-latency streaming for real-time, interactive experiences."
			mobile-inline
			:usage="subSecondHeaderUsage"
		>
			<Tooltip v-if="showSubSecondCreateButton" :active="atSubSecondLimit" wrap-trigger :html="true" :hide="1000" :content="subSecondCreateDisabledTitle">
				<FormButton class="gap-1" size="md" :disabled="isSubSecondCreateDisabled" @click="showCreateSubSecondStreamModal">
					<icon-base icon-name="plus" classIcon="mr-2"><icon-plus /></icon-base>
					New <span class="hidden md:inline">Sub-Second Stream</span>
				</FormButton>
			</Tooltip>
		</PageHeader>
		<Spinner v-if="loadingStatus" text="Loading your sub-second streams…" classes="text-surface-8 mh-60"
			spinner-color="var(--c-surface-8)" spinner-size="15px" />
		<EmptyState v-else-if="subSecondStreams && subSecondStreams.length === 0">
			<template #illustration>
				<img width="120" src="@/assets/empty-playout-placeholder.png" alt="Playout Line Up">
			</template>
			<template v-if="hasLiveSubscription.enabled && maxSubSecondStreams > 0">
				<h3 class="text-xl font-semibold text-surface-text-2 !mb-2">Create your first sub-second stream</h3>
				<p class="mb-0 max-w-sm text-sm leading-snug text-surface-8">Stream with sub-second latency for real-time, interactive experiences.</p>
				<div class="mt-6 flex w-full flex-col items-center gap-2">
					<FormButton type="secondary" @click="showTutorialModal" class="w-64 flex items-center justify-center gap-x-2">
						<div class="rounded-full !border !border-surface-8 size-4 flex items-center justify-center">
							<icon-base iconColor="none"
								class="-mr-1 text-dark-8" width="12" height="14">
								<icon-lightning />
							</icon-base>
						</div>
						Watch demo
					</FormButton>
					<Tooltip :active="atSubSecondLimit" wrap-trigger :html="true" :hide="1000">
						<FormButton :disabled="atSubSecondLimit"
							@click="showCreateSubSecondStreamModal" class="w-64 flex items-center justify-center gap-x-2">
							<div class="rounded-full !border !border-surface-8 size-4 flex items-center justify-center">
								<icon-base iconColor="none"
									class="-mr-1 text-dark-8" width="12" height="14">
									<icon-lightning />
								</icon-base>
							</div>
							New Sub-Second Stream
						</FormButton>
						<template #content>
							<span class="text-surface-8 text-sm" v-html="subSecondCreateDisabledTitle" />
						</template>
					</Tooltip>
				</div>
			</template>
			<template v-else>
				<h3 class="text-xl font-semibold text-surface-text-2 !mb-2">Upgrade to unlock Sub-Second Streaming</h3>
				<p class="mb-0 max-w-sm text-sm leading-snug text-surface-8">Deliver ultra-low-latency, real-time streaming for interactive experiences. It's not included in your current plan.</p>
				<div class="mt-6 flex w-full flex-col items-center">
					<FormButton to="/subscribe?category=live" size="lg">
						Upgrade
					</FormButton>
				</div>
			</template>
		</EmptyState>
		<div v-else>
			<section class="mt-6">
				<ResponsiveToolbar class="my-3">
					<template v-if="showListControls">
						<DropDownMenu ref="sortDropdown" type="secondary" position="left">
							<template #toggle-button>
								<span class="text-surface-8">Sort by:&nbsp;</span>
								<span class="text-white">{{ sortByLabel }}</span>
								<icon-base class="!ml-1 shrink-0 text-surface-8">
									<icon-chevron-up-down />
								</icon-base>
							</template>
							<template #menu-items>
								<DropDownMenuItem :active="sortBy === 'Oldest'" @click="changeSortBy('Oldest')">
									Oldest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Oldest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Newest'" @click="changeSortBy('Newest')">
									Newest
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Newest'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'A to Z'" @click="changeSortBy('A to Z')">
									A to Z
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'A to Z'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
								<DropDownMenuItem :active="sortBy === 'Z to A'" @click="changeSortBy('Z to A')">
									Z to A
									<icon-base class="shrink-0 !ml-2 text-surface-7" v-if="sortBy === 'Z to A'" fill="none">
										<icon-checkmark />
									</icon-base>
								</DropDownMenuItem>
							</template>
						</DropDownMenu>
						<ListingSearchInput v-model="searchWidgetInput" />
					</template>
					<ViewModeSwitcher class="shrink-0" />
				</ResponsiveToolbar>
			</section>
			<section class="mt-6">
				<div class="grid auto-rows-fr" :class="shouldShowCompactModeStreams ? '!grid-cols-1 gap-1.5' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4'">
					<div v-for="stream in subSecondStreamsFiltered" :key="stream._id" class="grid-item">
						<stream-card-view :stream="stream" :compact-mode="shouldShowCompactModeStreams"
							@delete-stream="onStreamDeleteRequest" />
					</div>
					<div
						v-if="!searchWidgetInput && showSubSecondCreateButton && (!memberPermission || memberPermission.liveStreamManage)"
						class="grid-item"
					>
						<ListingCreateTile
							label="Sub-Second Stream"
							:view="shouldShowCompactModeStreams ? 'list' : 'thumbnail'"
							:used="subSecondStreams.length"
							:limit="subSecondLimit"
							:limit-reached="atSubSecondLimit"
							@create="showCreateSubSecondStreamModal"
						/>
					</div>
				</div>
			</section>
		</div>
		<add-sub-second-stream-modal @new-channel="onNewStream" />
		<demo-video-modal modalId="modal_tutorial" videoid="48ofjE3WZ064-SuIElpIr" />
		<confirm-modal message="Would you like to delete this stream and all of its content?" modal-id="delete-stream"
			modal-type="danger" @modal-confirm="onStreamDeleteConfirm" />
	</div>
</template>


<style scoped>
</style>

